import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, CalendarClock, Zap, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("bc_admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const StartMeetingButton = ({ booking, compact = false }) => {
  const [busy, setBusy] = useState(false);
  if (!booking?.zoom_meeting_id && !booking?.zoom_start_url) return null;
  if (booking.status === "Cancelled") return null;

  const start = async (e) => {
    e.stopPropagation();
    const win = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings/${booking.id}/zoom-start`, { headers: authHeaders() });
      if (win) win.location.href = data.start_url;
      else window.location.href = data.start_url;
    } catch {
      if (win) win.close();
      toast.error("Could not get the Zoom start link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 bg-red-500 text-white hover:bg-red-600 transition-colors ${compact ? "text-[10px] px-2 py-1 uppercase tracking-[0.12em]" : "text-xs px-3 py-2"}`}
      style={{ borderRadius: 0 }}
      title="Start the Zoom meeting as host"
      data-testid={compact ? `booking-row-start-${booking.id}` : "booking-zoom-start"}
    >
      {busy ? <Loader2 size={compact ? 11 : 13} className="animate-spin" /> : <Video size={compact ? 11 : 13} />}
      {compact ? "Start" : "Start meeting as host"}
    </button>
  );
};

export const RescheduleCard = ({ booking, onRescheduled }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setOpen(false); setDate(""); setTime(""); setSlots([]); }, [booking?.id]);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    axios.get(`${API}/booking/slots`, { params: { date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  if (!booking || booking.status === "Cancelled") return null;

  const pickNext = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings/next-slot`, { headers: authHeaders() });
      setDate(data.date);
      setTime(data.time);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No free slots found");
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.id}/reschedule`, { date, time }, { headers: authHeaders() });
      toast.success(data.zoom_synced ? "Rescheduled · Zoom updated · client notified" : "Rescheduled · client notified (Zoom not synced)");
      onRescheduled(data);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reschedule");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 transition-colors" style={{ borderRadius: 0 }} data-testid="booking-reschedule-open">
        <CalendarClock size={13} /> Reschedule
      </button>
    );
  }

  return (
    <div className="mt-4 border border-white/10 bg-black/30 p-4" style={{ borderRadius: 0 }} data-testid="booking-reschedule-card">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow flex items-center gap-1.5"><CalendarClock size={12} className="text-red-500" /> Reschedule</div>
        <button onClick={pickNext} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-red-600/60 text-red-300 hover:bg-red-500/10" style={{ borderRadius: 0 }} data-testid="booking-reschedule-next">
          <Zap size={11} /> Next available
        </button>
      </div>
      <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => { setDate(e.target.value); setTime(""); }} className="w-full bg-zinc-900/60 border border-white/10 text-sm text-white px-3 py-2 [color-scheme:dark]" style={{ borderRadius: 0 }} data-testid="booking-reschedule-date" />
      {date && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="booking-reschedule-slots">
          {loadingSlots ? <span className="text-xs text-zinc-500">Loading slots…</span>
            : slots.length === 0 ? <span className="text-xs text-zinc-500">No free slots on this day.</span>
            : slots.map((s) => (
              <button key={s} onClick={() => setTime(s)} className={`text-xs px-2.5 py-1.5 border transition-colors ${time === s ? "bg-red-500 border-red-500 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`} style={{ borderRadius: 0 }} data-testid={`booking-reschedule-slot-${s}`}>{s}</button>
            ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-2">
        <button onClick={submit} disabled={!date || !time || saving} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-red-500 text-white hover:bg-red-600 disabled:opacity-40" style={{ borderRadius: 0 }} data-testid="booking-reschedule-confirm">
          {saving && <Loader2 size={12} className="animate-spin" />} Confirm {date && time ? `${date} · ${time} IST` : ""}
        </button>
        <button onClick={() => setOpen(false)} className="text-xs px-3 py-2 text-zinc-400 hover:text-white" data-testid="booking-reschedule-cancel">Cancel</button>
      </div>
      <p className="mt-2 text-[11px] text-zinc-500">Moves the Zoom meeting and sends the client an updated email (.ics) + WhatsApp.</p>
    </div>
  );
};
